'use client';

import { useEffect, useState } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { useForumStore } from '@/store/forumStore';
import { ActivityLogger } from '@/lib/activityLog';
import { detectCrisisKeywords } from '@/utils';
import { FilterChips, type FilterOption, CrisisBanner, SearchInput } from '@/components/ui';
import { GroupTopicCard } from '@/components/group-tabs';

interface EventForumTabProps {
  eventDocumentId: string;
  eventTitle: string;
  canParticipate: boolean;
}

export function EventForumTab({ eventDocumentId, eventTitle, canParticipate }: EventForumTabProps) {
  const { t } = useLanguage();
  const { currentUser } = useAuth();
  const { categories, eventTopics, loading, fetchCategories, fetchEventTopics, createTopic } = useForumStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategorySlug, setSelectedCategorySlug] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDetails, setNewDetails] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [crisisBannerDismissed, setCrisisBannerDismissed] = useState(false);

  const hasCrisisContent = !crisisBannerDismissed && detectCrisisKeywords(`${newTitle} ${newDetails}`);
  const topics = eventTopics[eventDocumentId] || [];

  useEffect(() => {
    fetchCategories();
    fetchEventTopics(eventDocumentId);
  }, [eventDocumentId, fetchCategories, fetchEventTopics]);

  const filteredTopics = topics
    .filter((topic) => {
      const topicContent = (topic.content?.[0] as { children?: { text?: string }[] })?.children?.[0]?.text || '';
      const normalizedQuery = searchQuery.toLowerCase();
      const matchesSearch = topic.title.toLowerCase().includes(normalizedQuery) ||
        topicContent.toLowerCase().includes(normalizedQuery) ||
        topic.category?.name.toLowerCase().includes(normalizedQuery);
      const matchesCategory = !selectedCategorySlug || topic.category?.slug === selectedCategorySlug;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const categoryOptions: FilterOption<string | null>[] = [
    { label: t.common.all, value: null },
    ...categories.map((category) => ({ label: category.name, value: category.slug })),
  ];

  const handleCreateTopic = async () => {
    if (!currentUser || !newTitle.trim() || !newCategory || !canParticipate) {
      return;
    }

    setSubmitting(true);
    const success = await createTopic({
      title: newTitle.trim(),
      content: newDetails.trim(),
      categoryDocumentId: newCategory,
      eventDocumentId,
    });

    if (success) {
      ActivityLogger.createForumTopic(currentUser, newTitle.trim());
      setShowModal(false);
      setNewTitle('');
      setNewDetails('');
      setNewCategory('');
      setCrisisBannerDismissed(false);
    }

    setSubmitting(false);
  };

  return (
    <div className="space-y-4">
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          <div className="relative bg-background rounded-lg shadow-lg w-full max-w-md mx-4 p-6">
            <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-foreground-muted hover:text-foreground">
              <X size={20} />
            </button>
            <h2 className="text-lg font-semibold text-foreground mb-1">{t.groupTabs.startDiscussion}</h2>
            <p className="text-sm text-foreground-muted mb-4">{t.groupTabs.postToForum.replace('{groupName}', eventTitle)}</p>
            {hasCrisisContent && <CrisisBanner onDismiss={() => setCrisisBannerDismissed(true)} />}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t.common.title} <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(event) => setNewTitle(event.target.value)}
                  placeholder={t.groupTabs.enterTopicTitle}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm focus:outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t.common.category} <span className="text-error">*</span>
                </label>
                <select
                  value={newCategory}
                  onChange={(event) => setNewCategory(event.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm focus:outline-none focus:border-primary"
                >
                  <option value="">{t.groupTabs.selectCategory}</option>
                  {categories.map((category) => (
                    <option key={category.documentId} value={category.documentId}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t.groupTabs.details}</label>
                <textarea
                  value={newDetails}
                  onChange={(event) => setNewDetails(event.target.value)}
                  placeholder={t.groupTabs.enterTopicDetails}
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm focus:outline-none focus:border-primary resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm font-medium text-foreground hover:bg-background-secondary rounded-lg transition-colors">
                {t.common.cancel}
              </button>
              <button
                onClick={handleCreateTopic}
                disabled={!newTitle.trim() || !newCategory || submitting}
                className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? t.groupTabs.posting : t.groupTabs.post}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">{t.groupTabs.discussions}</h2>
        {canParticipate && (
          <button onClick={() => setShowModal(true)} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium whitespace-nowrap hover:opacity-90 transition">
            {t.groupTabs.startDiscussion}
          </button>
        )}
      </div>
      <div className="mb-4"><SearchInput value={searchQuery} onChange={setSearchQuery} placeholder={t.groupTabs.searchDiscussions} /></div>
      {/* FilterChips owns its scrolling row; a wrapper scroll container would clip its arrows. */}
      <div className="mb-4">
        <FilterChips options={categoryOptions} selected={selectedCategorySlug} onChange={setSelectedCategorySlug} variant="tier2" scrollPrevLabel={t.common.scrollFiltersLeft} scrollNextLabel={t.common.scrollFiltersRight} />
      </div>

      {loading ? (
        <div className="p-8 text-center text-foreground-muted">{t.common.loading}</div>
      ) : filteredTopics.length === 0 ? (
        <div className="text-center py-12 text-foreground-muted border border-border rounded-lg bg-background-secondary">
          <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
          <p>{t.groupTabs.noDiscussions.replace('{groupName}', eventTitle)}</p>
          {canParticipate && <p className="text-sm mt-1">{t.groupTabs.beFirstToStart}</p>}
        </div>
      ) : (
        filteredTopics.map((topic) => (
          <GroupTopicCard
            key={topic.id}
            topic={topic}
            eventDocumentId={eventDocumentId}
            searchQuery={searchQuery}
            isMember={canParticipate}
          />
        ))
      )}
    </div>
  );
}
